import React, { useState } from 'react'
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import { useCreateReportMutation, useGetTemplatesQuery } from '@/core/services/report'
import CircularProgress from '@mui/material/CircularProgress'
import { Consultation } from '@/core/models/consultation.model'
import { t, Trans } from '@lingui/macro'
import { skipToken } from '@reduxjs/toolkit/query'

interface CreateReportModalProps {
  consultation: Consultation
  userId: string
}

export default function CreateReportModal({ consultation, userId }: CreateReportModalProps) {
  const { data: templates } = useGetTemplatesQuery(userId ? { userId } : skipToken, { refetchOnMountOrArgChange: true })
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [createReport, { isLoading }] = useCreateReportMutation()

  const handleSubmit = async () => {
    if (selectedTemplate && consultation.transcription) {
      try {
        await createReport({
          template: selectedTemplate,
          consultation: consultation._id,
          submittedBy: userId,
          transcription: consultation.transcription,
          patient: consultation.patient._id,
          responses: [],
        }).unwrap()
        setReportModalOpen(false)
      } catch (error) {
        console.error('Failed to create report:', error)
      }
    }
  }

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setReportModalOpen(true)}>
        <Trans>Create a report</Trans>
      </Button>

      <Dialog open={reportModalOpen} onClose={() => setReportModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t`Create a new report`}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            <Trans>Choose a report template to get started</Trans>
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="template-select-label">
              <Trans>Report template</Trans>
            </InputLabel>
            <Select labelId="template-select-label" value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)} label="Report template">
              {templates?.map((template) => (
                <MenuItem key={template._id} value={template._id}>
                  {template.isDefault ? (
                    <>
                      {template.title}
                      <Chip sx={{ ml: 1 }} label={t`default`} color="secondary" size="small" />
                    </>
                  ) : (
                    template.title
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportModalOpen(false)}>{t`Cancel`}</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!selectedTemplate || !consultation.transcription || isLoading}>
            {isLoading ? (
              <>
                <CircularProgress size="20px" color="inherit" />
                <Trans>Generation in progress...</Trans>
              </>
            ) : (
              <Trans>Create</Trans>
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
